import { useRef, useCallback } from 'react';
import type { Wall, WallBlockingType, DoorState, ClientMessage, ServerMessage } from '@roll1/shared';
import type { CanvasEngine } from './useCanvasEngine';
import type { DistributiveOmit } from './useMultiplayer';

type WallMessage = DistributiveOmit<ClientMessage, 'id' | 'timestamp'>;

export interface UseWallsReturn {
  walls: React.MutableRefObject<Wall[]>;
  /** Monotonically increasing counter — increments on any wall/door mutation. Used as cache key for visibility. */
  wallsVersion: React.MutableRefObject<number>;
  createWall: (wall: Omit<Wall, 'id'>, pageId: string) => void;
  updateWall: (wallId: string, changes: Partial<Pick<Wall, 'blocking' | 'doorState' | 'color' | 'x1' | 'y1' | 'x2' | 'y2'>>, pageId: string) => void;
  deleteWall: (wallId: string, pageId: string) => void;
  chainWalls: (points: Array<{ x: number; y: number }>, blocking: WallBlockingType, pageId: string, doorState?: DoorState) => void;
  toggleDoor: (wallId: string, pageId: string) => void;
  /** Handle incoming server message. Returns true if message was handled. */
  handleMessage: (msg: ServerMessage) => boolean;
}

export function useWalls(
  engine: CanvasEngine,
  sendMessage: (msg: WallMessage) => void,
  isGM: boolean,
): UseWallsReturn {
  const walls = useRef<Wall[]>([]);
  const wallsVersion = useRef(0);

  const createWall = useCallback((wall: Omit<Wall, 'id'>, pageId: string) => {
    if (!isGM) return;
    sendMessage({ type: 'wall:create', pageId, wall });
  }, [isGM, sendMessage]);

  const updateWall = useCallback((wallId: string, changes: Partial<Pick<Wall, 'blocking' | 'doorState' | 'color' | 'x1' | 'y1' | 'x2' | 'y2'>>, pageId: string) => {
    if (!isGM) return;
    sendMessage({ type: 'wall:update', pageId, wallId, changes });
  }, [isGM, sendMessage]);

  const deleteWall = useCallback((wallId: string, pageId: string) => {
    if (!isGM) return;
    sendMessage({ type: 'wall:delete', pageId, wallId });
  }, [isGM, sendMessage]);

  const chainWalls = useCallback((
    points: Array<{ x: number; y: number }>,
    blocking: WallBlockingType,
    pageId: string,
    doorState?: DoorState,
  ) => {
    if (!isGM) return;
    sendMessage({ type: 'wall:chain', pageId, points, blocking, doorState });
  }, [isGM, sendMessage]);

  const toggleDoor = useCallback((wallId: string, pageId: string) => {
    if (!isGM) return;
    sendMessage({ type: 'door:toggle', pageId, wallId });
  }, [isGM, sendMessage]);

  const handleMessage = useCallback((msg: ServerMessage): boolean => {
    switch (msg.type) {
      case 'wall:created':
        walls.current.push(msg.wall);
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      case 'wall:updated': {
        const idx = walls.current.findIndex(w => w.id === msg.wall.id);
        if (idx >= 0) walls.current[idx] = msg.wall;
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      }
      case 'wall:deleted':
        walls.current = walls.current.filter(w => w.id !== msg.wallId);
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      case 'walls:created':
        walls.current.push(...msg.walls);
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      case 'door:toggled': {
        const idx = walls.current.findIndex(w => w.id === msg.wall.id);
        if (idx >= 0) walls.current[idx] = msg.wall;
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      }
      case 'wall:auto-synced': {
        // Remove walls by IDs
        if (msg.removed.length > 0) {
          const removedSet = new Set(msg.removed);
          walls.current = walls.current.filter(w => !removedSet.has(w.id));
        }
        // Add new walls
        if (msg.added.length > 0) {
          walls.current.push(...msg.added);
        }
        wallsVersion.current++;
        engine.markDirty('ui');
        engine.markDirty('fog');
        return true;
      }
      default:
        return false;
    }
  }, [engine]);

  return {
    walls,
    wallsVersion,
    createWall,
    updateWall,
    deleteWall,
    chainWalls,
    toggleDoor,
    handleMessage,
  };
}
